/** 
 * @namespace AlertDialog
 * @memberof Promega.Shell
 * @description The AlertDialog is a large, full-width banner that can be displayed at the top of the site above all other content.
 * Examples include the country message that is shown on first visit, as well as the "Testing Environment" message that can be seen in non-production environments such as [test.promega.com]{@link https://test.promega.com}.
 * Instances of the AlertDialog are typically created and populated within Sitecore: the methods provided here only enable the showing or hiding of an AlertDialog that is already present in the page markup.
 * 
 * @example <caption>An alert dialog is typically shown at the top of the page. It must have class <code>alert-container</code>.<br><br>
 * The alert dialog spans the width of the browser window by default, so you will typically want to include a content-width container
 * within it:</caption>
 * <div class="alert-container">
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption>If you include a <code>&lt;button&gt;</code> element with class <code>alert-container__dismiss-button</code>,
 * then clicking this button will cause the alert dialog to be dismissed:</caption>
 * <div class="alert-container">
 *   <div class="alert-container__content content-width">
 *     <p>Alert dialog content goes here.</p>
 *     <button type="button" class="button button--light-gray alert-container__dismiss-button</button>
 *   </div>
 * </div>
 * 
 * @example <caption>Backgrounds are also supported:</caption>
 * <div class="alert-container has-background">
 *   <div class="background-element background--gray-50"></div>
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption>You can set attribute <code>data-autoshow="true"</code> to have the alert dialog automatically be shown on page load:</caption>
 * <div class="alert-container" data-autoshow="true">
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption> You can also specify the name of a cookie to set in attribute <code>data-set-cookie-on-hide</code> in order to have a cookie
 * set when the user dismisses the dialog. Overrides attribute <code>data-autoshow</code>. Useful for making sure an automatic dialog stays
 * dismissed on subsequent page loads.<br><br>
 * On subsequent pages loads, if the alert dialog has a configuration of <code>data-set-cookie-on-hide</code> that matches a cookie,
 * the dialog will not be shown even if <code>data-autoshow="true"</code> is set. You can still manually show the dialog by calling
 * <code>Promega.Shell.AlertDialog.showDialog(elementContainerGoesHere)</code> in JavaScript.
 * <div class="alert-container" data-set-cookie-on-hide='{"name": "myCookieName", "value": "The value to set for the cookie"}'>
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 */

import isAnimationSupported from '../util/isAnimationSupported';
import trackEvent from '../util/trackEvent';
import cookie from 'cookie_js';

const ALERT_CONTAINER_CLASS = "alert-container",
    ALERT_CONTAINER_VISIBLE_CLASS = "alert-container--visible",
    ALERT_ACCORDION_TITLE_CLASS = "alert-accordion-title",
    ALERT_ACCORDION_PANEL = "alert-accordion-panel",
    DISMISS_BUTTON_CLASS = "alert-container__dismiss-button",
    ACCEPTALL_BUTTON_CLASS = "alert-container__acceptall-button";
    /* SELECTCOOKIES_BUTTON_CLASS = "alert-container__selectcookies-button"; */

// Private functions/listeners

function addAlertDialogMobileExpand (event) {
    if (event.target && event.target.classList.contains(ALERT_ACCORDION_TITLE_CLASS)) {
        const accordionTitle = event.target;
        const accordionPanel = event.target.nextElementSibling;

        accordionTitle.classList.toggle("alert-accordion-title-expand");

        if (accordionPanel.style.maxHeight) {
            accordionPanel.style.maxHeight = null;
        } else {
            accordionPanel.style.maxHeight = accordionPanel.scrollHeight + "px";
        }

    }
}

function alertDialogClicked (event) {
    if (event.target && event.target.classList.contains(DISMISS_BUTTON_CLASS)) {
        const containerParent = event.target.closest(`.${ALERT_CONTAINER_CLASS}`);
        if (containerParent) {
            hideDialog(containerParent);
            trackEvent("Components", "Alert dialog - dismissed");
        }
    }
    if (event.target && event.target.classList.contains(ACCEPTALL_BUTTON_CLASS)) {
        const containerParent = event.target.closest(`.${ALERT_CONTAINER_CLASS}`);
        if (containerParent) {
            hideDialog(containerParent);
            trackEvent("Components", "Alert dialog - acceptall");
        }
    }
    /*
    if (event.target && event.target.classList.contains(SELECTCOOKIES_BUTTON_CLASS)) {
        alert('This will show the Select Cookies dialog');
        trackEvent("Components", "Alert dialog - selectcookies");
    }
    */
}

function hideAlertDialogAfterTransition(event) {
    const containerEl = event.target;
    if (containerEl.classList.contains(ALERT_CONTAINER_CLASS)) {
        containerEl.classList.remove(ALERT_CONTAINER_VISIBLE_CLASS);
        containerEl.style.transition = "";
        containerEl.style.marginBottom = "";
        containerEl.removeEventListener("transitionend", hideAlertDialogAfterTransition);
    }
}

// API

/**
 * @function showDialog
 * @param {HTMLElement} containerEl The AlertDialog to show.
 * @memberof Promega.Shell.AlertDialog
 * @description Shows the given AlertDialog.
 */
export function showDialog(containerEl) {
    if (containerEl && containerEl.classList.contains(ALERT_CONTAINER_CLASS) && !containerEl.classList.contains(ALERT_CONTAINER_VISIBLE_CLASS)) {
        containerEl.classList.add(ALERT_CONTAINER_VISIBLE_CLASS);

    }
}

/**
 * @function hideDialog
 * @param {HTMLElement} containerEl The AlertDialog to hide.
 * @memberof Promega.Shell.AlertDialog
 * @description Hides the given AlertDialog.
 */
export function hideDialog (containerEl) {
    if (containerEl && containerEl.classList.contains(ALERT_CONTAINER_CLASS) && containerEl.classList.contains(ALERT_CONTAINER_VISIBLE_CLASS)) {
        if (isAnimationSupported()) {
            const boundingRect = containerEl.getBoundingClientRect();
            containerEl.style.transition = "margin-bottom .3s ease";
            containerEl.addEventListener("transitionend", hideAlertDialogAfterTransition);
            containerEl.style.marginBottom = `-${boundingRect.height}px`;
        }
        else hideAlertDialogAfterTransition({ target: containerEl });

        let setCookieOnHide = containerEl.getAttribute("data-set-cookie-on-hide");
        if (setCookieOnHide) {
            setCookieOnHide = JSON.parse(setCookieOnHide);
            if (setCookieOnHide.maxAge) {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { "max-age": setCookieOnHide.maxAge, "path": "/" });
                console.warn("Cookies max-age option does not appear to be supported any longer (03/02/2021 CPAULY)");
            }
            else if (setCookieOnHide.expires) {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { "expires": setCookieOnHide.expires, "path": "/" });
            }
            else {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { path: "/" });
            }
        }
    }
}

// Init code

if (typeof document !== "undefined") {
    const allAlertDialogs = document.getElementsByClassName(ALERT_CONTAINER_CLASS);
    for (let i = 0; i < allAlertDialogs.length; i++) {
        let setCookieOnHide = allAlertDialogs[i].getAttribute("data-set-cookie-on-hide"),
            autoshow = allAlertDialogs[i].getAttribute("data-autoshow");

        if (autoshow) {
            // Automatically show the dialog, unless the cookie that is set on hide is already set
            let alreadyHidden = false;
            if (setCookieOnHide) {
                setCookieOnHide = JSON.parse(setCookieOnHide);
                if (cookie.get(setCookieOnHide.name) === setCookieOnHide.value) {
                    alreadyHidden = true;
                }
            }
            if (!alreadyHidden) showDialog(allAlertDialogs[i]);
        }
    }

    document.addEventListener("click", addAlertDialogMobileExpand);
    document.addEventListener("click", alertDialogClicked);
}
